import React, { useLayoutEffect, useRef, useState } from 'react';
import { LazyHydrate } from 'src/components/AppShell/LazyHydrate';

import { loadFooter } from './deferredLoader';
import classes from './FooterSlot.scss';

export const FOOTER_SLOT_FALLBACK_MIN_HEIGHT = 582;
const CACHE_KEY_PREFIX = 'wwe:footer-slot-min-height';

function viewportBucket(): string {
    if (typeof window === 'undefined') return 'ssr';
    const width = window.innerWidth;
    if (width < 600) return 'mob';
    if (width < 900) return 'tab';
    if (width < 1200) return 'desk';
    return 'wide';
}

function cacheKey(): string {
    return `${CACHE_KEY_PREFIX}:${viewportBucket()}`;
}

function readCachedFooterHeight(): number | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
        const parsed = parseInt(sessionStorage.getItem(cacheKey()) || '', 10);
        return parsed > 0 ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function writeCachedFooterHeight(height: number): void {
    try {
        sessionStorage.setItem(cacheKey(), String(height));
    } catch {
        // Ignore quota / private-mode failures.
    }
}

function measureFooterHeight(slot: HTMLElement): number {
    const footer = slot.querySelector('footer');
    if (!(footer instanceof HTMLElement)) return 0;
    return Math.round(footer.getBoundingClientRect().height);
}

export const FooterSlot: React.FC = () => {
    const slotRef = useRef<HTMLDivElement>(null);
    const [minHeight, setMinHeight] = useState(FOOTER_SLOT_FALLBACK_MIN_HEIGHT);

    useLayoutEffect(() => {
        const slot = slotRef.current;
        if (!slot) return;

        const cached = readCachedFooterHeight();
        if (cached) {
            setMinHeight(cached);
        }

        let observedFooter: Element | null = null;
        let resizeObserver: ResizeObserver | null = null;

        const sync = () => {
            const footer = slot.querySelector('footer');
            if (resizeObserver && footer && footer !== observedFooter) {
                if (observedFooter) resizeObserver.unobserve(observedFooter);
                resizeObserver.observe(footer);
                observedFooter = footer;
            }

            const height = measureFooterHeight(slot);
            if (height > 0) {
                setMinHeight((current) => (current === height ? current : height));
                writeCachedFooterHeight(height);
            }
        };

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(sync);
            resizeObserver.observe(slot);
        }

        const mutationObserver = new MutationObserver(sync);
        mutationObserver.observe(slot, { childList: true, subtree: true });

        sync();
        window.addEventListener('resize', sync);

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, []);

    return (
        <div ref={slotRef} className={classes.footerSlot} style={{ minHeight }}>
            <LazyHydrate sectionId="site-footer" load={loadFooter} />
        </div>
    );
};
